import CloseIcon from "@mui/icons-material/Close";
import { Alert, IconButton, Snackbar } from "@mui/material";
import Slide from "@mui/material/Slide";
import React from "react";
import { useTranslation } from "react-i18next";
import ErrorFullScreenMessage from "../components/fullScreenMessage/ErrorFullScreenMessage";
import Loader from "../components/loader/Loader";
import { PERMISSIONS } from "../enums/QueryKeys";
import { useFetch } from "../hooks/Fetching";
import useUser from "../hooks/useUser";
import { isEmptyString } from "../utils/Functions";

export const GlobalDataContext = React.createContext();
export default function GlobalData({ children }) {
  const { t } = useTranslation();
  const [openSnackbar, setOpenSnakbar] = React.useState(true);
  const { profile, roles, currentClinic, isLoading: userLoading } = useUser();

  const missingPhone = isEmptyString(profile.phoneNumber);

  const {
    data: permissions,
    isLoading: permissionsLoading,
    error: permissionError,
  } = useFetch("/permissions/user/", profile?.id, PERMISSIONS + profile?.id);



  const getPermissionTitles = React.useCallback((permissions) => {
    const titles = {};
    permissions?.forEach((element) => {
      if (element) {
        titles[element] = element;
      }
    });

    return titles;
  }, []);
  const getPermissionTitlesArray = React.useCallback((permissions) => {
    const titles = [];
    permissions?.forEach((element) => {
      if (element) {
        titles.push(String(element));
      }
    });

    return titles;
  }, []);

  const getPermissionsMap = React.useCallback((permissions) => {
    const map = {};

    permissions?.forEach((element) => {
      const title = element;
      if (title) {
        const parts = title.split("_");
        if (parts.length === 2) {
          const [action, template] = parts;
          if (!map[template]) {
            map[template] = [];
          }
          map[template].push(action);
        }
      }
    });

    return map;
  }, []);

  //This is still a work in progress, not implemented anywhere
  function permissionsMap(permissions) {
    const map = {};
    Object.keys(permissions).forEach((element) => {
      const id = element;
      const title = permissions[id];
      if (title && id) {
        const parts = title.split("_");
        if (parts.length >= 2) {
          var action = parts[0];
          if (action === "CHANGE") {
            const template = parts[1];
            if (!map[template]) {
              map[template] = [];
            }
            action = action + "_" + parts[2];
            map[template].push({ id, title, action });
          } else {
            const template = parts.slice(1).join("_");
            if (!map[template]) {
              map[template] = [];
            }
            map[template].push({ id, title, action });
          }
        }
      }
    });

    return map;
  }

  const getPermissionMapValue = React.useCallback(
    (key) => {
      return getPermissionsMap(permissions)[String(key).toUpperCase()];
    },
    [getPermissionsMap, permissions]
  );

  const hasAction = React.useCallback((map, mapKey, mapArrayValue) => {
    return (
      map[String(mapKey).toUpperCase()]?.includes(
        String(mapArrayValue).toUpperCase()
      ) ?? false
    );
  }, []);

  const isPermissionAllowed = React.useCallback(
    (permission) => {
      if (roles.master) {
        return true;
      }

      const perm = String(permission);
      var bool = false;

      if (permissions != null) {
        permissions.forEach((data) => {
          if (String(data) === perm) {
            bool = true;
          }
        });
      }

      return Boolean(bool);
    },
    [roles.master, permissions]
  );

  const values = React.useMemo(
    () => ({
      permissions,
      getPermissionTitles,
      hasAction,
      getPermissionsMap,
      getPermissionMapValue,
      permissionsMap,
      getPermissionTitlesArray,
      isPermissionAllowed,
    }),
    [
      getPermissionMapValue,
      getPermissionTitles,
      getPermissionTitlesArray,
      getPermissionsMap,
      hasAction,
      isPermissionAllowed,
      permissions,
    ]
  );

  function handleCloseSnackbar() {
    setOpenSnakbar(false);
  }

  const action = (
    <IconButton
      size="small"
      color="inherit"
      onClick={() => handleCloseSnackbar()}
      title={t("Close")}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  if (!userLoading) {
    if (!roles.master && missingPhone) {
      return (
        <GlobalDataContext.Provider value={values}>
          <Snackbar
            TransitionComponent={Slide}
            sx={{ mt: 6 }}
            onClose={() => handleCloseSnackbar()}
            open={openSnackbar}
            action={action}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="warning" variant="outlined" sx={{ width: "100%" }}>
              {t("CantUseApplicationBeforeAddingAPhoneNumber")}
            </Alert>
          </Snackbar>
          {children}
        </GlobalDataContext.Provider>
      );
    }
  }
 
if (permissionsLoading) {
    return <Loader condition={true} />;
  }
  if (
    (permissionError) &&
    !permissionsLoading 
  ) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <GlobalDataContext.Provider value={values}>
      {children}
    </GlobalDataContext.Provider>
  );
}
